import { ChecklistItem } from '@app/app/_components/ChecklistItem/ChecklistItem';
import { ChoiceButtonOption } from '@app/app/_components/Input/ChoiceButtonInput';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { TransportPremiumsResponse } from '@app/domain/model/eshop/transport/TransportPremiumsResponse';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { PremiumCard } from '../../components/PremiumCard';
import { PremiumType } from '@app/domain/model/eshop/transport/PremiumType';
import { PremiumCode } from '@app/domain/model/eshop/transport/PremiumCode';
import { TransportPolicyValidity } from '@app/domain/model/eshop/transport/TransportPolicyValidity';
import { PolicyValidity } from '@app/domain/model/eshop/transport/PolicyValidity';
import { differenceInDays } from 'date-fns';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { AppParameters } from '@app/infrastructure/parameters/model/AppParameters';
import { InformationPopover } from '@app/app/_components/InformationPopover/InformationPopover';
import { QuestionCircleIcon } from '@app/app/_icons/QuestionCircleIcon';
import { Button } from '@app/app/_components/Button/Button';

function isPolicyValidLongTerm(policy?: Nullable<PolicyValidity>): boolean {
    if (!policy?.isValid || !policy.validUntil) {
        return false;
    }

    const expirationDate = new Date(policy.validUntil);
    const today = new Date();

    const daysUntilExpiration = differenceInDays(expirationDate, today);
    return daysUntilExpiration >= 30;
}

export const usePremiumsOptions = (
    premiums: TransportPremiumsResponse[],
    insuranceDuration: string,
    appParameters: AppParameters,
    policyValidity?: Nullable<TransportPolicyValidity>,
): ChoiceButtonOption[] => {
    const t = useTranslations();

    const options = useMemo(() => {
        const mtplPremium = premiums.find(
            premium => premium.riskPackageCode === PremiumCode.Mtpl && !premium.withCasco,
        );
        const mtplCascoPremium = premiums.find(
            premium => premium.riskPackageCode === PremiumCode.Mtpl && premium.withCasco,
        );
        const cascoPremiums = premiums.filter(premium => premium.riskPackageCode !== PremiumCode.Mtpl);

        const cheapestCasco =
            cascoPremiums.length > 0
                ? cascoPremiums.reduce((cheapest, current) => {
                      const currentPrice =
                          current.premiumAfterDiscount > 0 && current.premiumAfterDiscount !== current.premium
                              ? current.premiumAfterDiscount
                              : current.premium;
                      const cheapestPrice =
                          cheapest.premiumAfterDiscount > 0 && cheapest.premiumAfterDiscount !== cheapest.premium
                              ? cheapest.premiumAfterDiscount
                              : cheapest.premium;
                      return currentPrice < cheapestPrice ? current : cheapest;
                  }, cascoPremiums[0])
                : null;

        const isMtplValidLongTerm = isPolicyValidLongTerm(policyValidity?.mtplPolicyValidity);
        const isCascoValidLongTerm = isPolicyValidLongTerm(policyValidity?.cascoPolicyValidity);

        const result: ChoiceButtonOption[] = [];

        if ((mtplPremium || mtplCascoPremium) && cheapestCasco && !isCascoValidLongTerm && !isMtplValidLongTerm) {
            let mtplPrice = 0;
            let originalMtplPrice = 0;

            if (mtplCascoPremium) {
                mtplPrice =
                    mtplCascoPremium.premiumAfterDiscount > 0 &&
                    mtplCascoPremium.premiumAfterDiscount !== mtplCascoPremium.premium
                        ? mtplCascoPremium.premiumAfterDiscount
                        : mtplCascoPremium.premium;
                originalMtplPrice = mtplCascoPremium.premium;
            } else if (mtplPremium) {
                mtplPrice =
                    mtplPremium.premiumAfterDiscount > 0 && mtplPremium.premiumAfterDiscount !== mtplPremium.premium
                        ? mtplPremium.premiumAfterDiscount
                        : mtplPremium.premium;
                originalMtplPrice = mtplPremium.premium;
            }

            const cascoPrice =
                cheapestCasco.premiumAfterDiscount > 0 && cheapestCasco.premiumAfterDiscount !== cheapestCasco.premium
                    ? cheapestCasco.premiumAfterDiscount
                    : cheapestCasco.premium;

            const combinedPrice = mtplPrice + cascoPrice;

            const originalCombinedPrice = originalMtplPrice + cheapestCasco.premium;
            const hasCombinedDiscount = combinedPrice < originalCombinedPrice;
            const combinedDiscountPercentage = hasCombinedDiscount
                ? ((originalCombinedPrice - combinedPrice) / originalCombinedPrice) * 100
                : undefined;

            result.push({
                value: PremiumType.CascoMtpl,
                label: (
                    <PremiumCard
                        title={t('eshop.transport.policyType.mtpl_casco')}
                        price={combinedPrice}
                        originalPrice={hasCombinedDiscount ? originalCombinedPrice : undefined}
                        discountPercentage={combinedDiscountPercentage}
                        showFromPrefix={true}
                        insuranceDuration={insuranceDuration}
                        description={
                            <div className="flex flex-row items-center gap-2">
                                <span>{t('eshop.transport.mtplCascoPlansDescription')}</span>
                                <InformationPopover content={t('eshop.transport.mtplCascoPlansTooltip')}>
                                    <Button ignoreFormStatus preventDefault variant="text" className="inline-flex p-0">
                                        <QuestionCircleIcon className="size-4 text-neutral-500" />
                                    </Button>
                                </InformationPopover>
                            </div>
                        }
                    />
                ),
                details: (
                    <div className="flex flex-col gap-4">
                        <Paragraph isBold>{t('eshop.transport.policyDescriptions.whatWeCover')}</Paragraph>
                        <ChecklistItem>{t('eshop.transport.policyDescriptions.cascoMtpl.coverage1')}</ChecklistItem>
                        <ChecklistItem>{t('eshop.transport.policyDescriptions.cascoMtpl.coverage2')}</ChecklistItem>
                        <ChecklistItem>{t('eshop.transport.policyDescriptions.cascoMtpl.coverage3')}</ChecklistItem>
                        <TextButton
                            target="_blank"
                            href={appParameters.eshopInfoUrls.mtplCasco}
                            rightAddon={<ArrowRightIcon className="size-4" />}>
                            {t('general.readMore')}
                        </TextButton>
                    </div>
                ),
            });
        }

        if (mtplPremium && !isMtplValidLongTerm) {
            const hasDiscount =
                mtplPremium.premiumAfterDiscount > 0 && mtplPremium.premiumAfterDiscount !== mtplPremium.premium;
            const finalPrice = hasDiscount ? mtplPremium.premiumAfterDiscount : mtplPremium.premium;
            const discountPercentage = hasDiscount
                ? ((mtplPremium.premium - mtplPremium.premiumAfterDiscount) / mtplPremium.premium) * 100
                : undefined;

            result.push({
                value: PremiumType.Mtpl,
                label: (
                    <PremiumCard
                        title={t('eshop.transport.policyType.mtpl')}
                        price={finalPrice}
                        originalPrice={hasDiscount ? mtplPremium.premium : undefined}
                        discountPercentage={discountPercentage}
                        discountLabel={t('eshop.transport.save')}
                        insuranceDuration={insuranceDuration}
                        description={t('eshop.transport.mtplBasicDescription')}
                    />
                ),
                details: (
                    <div className="flex flex-col gap-4">
                        <Paragraph isBold>{t('eshop.transport.policyDescriptions.whatWeCover')}</Paragraph>
                        <ChecklistItem>{t('eshop.transport.policyDescriptions.mtpl.coverage1')}</ChecklistItem>
                        <ChecklistItem>{t('eshop.transport.policyDescriptions.mtpl.coverage2')}</ChecklistItem>
                        <ChecklistItem>{t('eshop.transport.policyDescriptions.mtpl.coverage3')}</ChecklistItem>
                        <ChecklistItem>{t('eshop.transport.policyDescriptions.mtpl.coverage4')}</ChecklistItem>
                        <TextButton
                            target="_blank"
                            href={appParameters.eshopInfoUrls.mtpl}
                            rightAddon={<ArrowRightIcon className="size-4" />}>
                            {t('general.readMore')}
                        </TextButton>
                    </div>
                ),
            });
        }

        if (cheapestCasco && !isCascoValidLongTerm) {
            const hasCascoDiscount =
                cheapestCasco.premiumAfterDiscount > 0 && cheapestCasco.premiumAfterDiscount !== cheapestCasco.premium;
            const cascoPrice = hasCascoDiscount ? cheapestCasco.premiumAfterDiscount : cheapestCasco.premium;
            const cascoDiscountPercentage = hasCascoDiscount
                ? ((cheapestCasco.premium - cheapestCasco.premiumAfterDiscount) / cheapestCasco.premium) * 100
                : undefined;

            result.push({
                value: PremiumType.Casco,
                label: (
                    <PremiumCard
                        title={t('eshop.transport.policyType.casco')}
                        price={cascoPrice}
                        originalPrice={hasCascoDiscount ? cheapestCasco.premium : undefined}
                        discountPercentage={cascoDiscountPercentage}
                        discountLabel={t('eshop.transport.save')}
                        showFromPrefix={true}
                        insuranceDuration={insuranceDuration}
                        description={
                            <div className="flex flex-row items-center gap-2">
                                <span>{t('eshop.transport.cascoPlansDescription')}</span>
                                <InformationPopover content={t('eshop.transport.cascoPlansTooltip')}>
                                    <Button preventDefault ignoreFormStatus variant="text" className="inline-flex p-0">
                                        <QuestionCircleIcon className="size-4 text-neutral-500" />
                                    </Button>
                                </InformationPopover>
                            </div>
                        }
                    />
                ),
                details: (
                    <div className="flex flex-col gap-4">
                        <Paragraph isBold>{t('eshop.transport.policyDescriptions.whatWeCover')}</Paragraph>
                        <ChecklistItem>{t('eshop.transport.policyDescriptions.casco.coverage1')}</ChecklistItem>
                        <ChecklistItem>{t('eshop.transport.policyDescriptions.casco.coverage2')}</ChecklistItem>
                        <ChecklistItem>{t('eshop.transport.policyDescriptions.casco.coverage3')}</ChecklistItem>
                        <TextButton
                            target="_blank"
                            href={appParameters.eshopInfoUrls.casco}
                            rightAddon={<ArrowRightIcon className="size-4" />}>
                            {t('general.readMore')}
                        </TextButton>
                    </div>
                ),
            });
        }

        return result;
    }, [
        premiums,
        policyValidity?.mtplPolicyValidity,
        policyValidity?.cascoPolicyValidity,
        t,
        insuranceDuration,
        appParameters.eshopInfoUrls.mtpl,
        appParameters.eshopInfoUrls.casco,
        appParameters.eshopInfoUrls.mtplCasco,
    ]);

    return options;
};
